import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import { toast } from 'react-toastify';
import { permissionsAPI, rolesAPI, usersAPI } from '../../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  Users, 
  Shield, 
  Search,
  Eye,
  UserPlus,
  Lock,
  Unlock
} from 'lucide-react';

const RolePermissionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const Tab = styled.button`
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.secondary};
  border-bottom: 2px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const RoleCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const RoleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const RoleTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const RoleDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 1rem 0;
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const PermissionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.checked ? props.theme.colors.primary + '20' : props.theme.colors.background.secondary};
  border: 1px solid ${props => props.checked ? props.theme.colors.primary : props.theme.colors.border.light};
`;

const PermissionCheckbox = styled.input`
  margin: 0;
`;

const PermissionLabel = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  flex: 1;
`;

const UserRoleCard = styled(Card)`
  padding: 1.5rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.full};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const UserEmail = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const RoleBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
`;

const ModalContent = styled.div`
  padding: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const RolePermissionSetup = () => {
  const { theme } = useTheme();
  const { user, hasPermission, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('roles');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [roles, setRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSaving, setUserSaving] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    permissions: []
  });

  const [userFormData, setUserFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    role: 'member',
    region: 'Addis Ababa',
    isActive: true
  });

  const slugify = (value) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const mapRoleFromApi = (role) => ({
    id: role.id,
    name: role.name,
    displayName: role.display_name || role.displayName || role.name,
    description: role.description || '',
    permissions: Array.isArray(role.permissions) ? [...new Set(role.permissions)] : [],
    permissionDetails: role.permission_details || [],
    userCount: role.user_count ?? 0,
    isActive: role.is_active ?? true,
    createdAt: role.created_at,
    updatedAt: role.updated_at,
  });

  const generatePassword = (length = 12) => {
    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i += 1) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const getRoleDisplayName = (roleName, roleSnapshot = roles) => {
    if (!roleName) return '—';
    const role = roleSnapshot.find(r => r.name === roleName);
    if (role) {
      return role.displayName;
    }
    return roleName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const fetchPermissions = async () => {
    try {
      const response = await permissionsAPI.list();
      const groups = Array.isArray(response.data) ? response.data : [];
      setPermissionGroups(
        groups.map(group => ({
          category: group.category || 'General',
          permissions: group.permissions || [],
          items: group.items || [],
        }))
      );
    } catch (error) {
      console.error('Failed to load permissions', error);
      toast.error('Failed to load permissions');
    }
  };

  const fetchUsers = async (roleSnapshot = roles) => {
    setUsersLoading(true);
    try {
      const response = await usersAPI.getAll({ per_page: 200 });
      const payload = Array.isArray(response.data?.data)
        ? response.data.data
        : (Array.isArray(response.data) ? response.data : []);

      const mapped = payload.map(user => ({
        id: user.id,
        username: user.username,
        name: user.full_name || `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.username,
        email: user.email,
        role: user.role,
        roleDisplayName: user.role_display_name || getRoleDisplayName(user.role, roleSnapshot),
        firstName: user.first_name,
        lastName: user.last_name,
        isActive: user.is_active ?? true,
        lastLogin: user.last_login,
        permissions: user.permissions || [],
      }));

      setUsers(mapped);
      return mapped;
    } catch (error) {
      console.error('Failed to load users', error);
      toast.error('Failed to load users');
      return [];
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await rolesAPI.list();
      const payload = Array.isArray(response.data) ? response.data : [];
      const mapped = payload.map(mapRoleFromApi);
      setRoles(mapped);
      return mapped;
    } catch (error) {
      console.error('Failed to load roles', error);
      toast.error('Failed to load roles');
      return [];
    }
  };

  useEffect(() => {
    const initialise = async () => {
      setLoading(true);
      try {
        await fetchPermissions();
        const rolesData = await fetchRoles();
        await fetchUsers(rolesData);
        await refreshUser();
      } finally {
        setLoading(false);
      }
    };

    initialise();
  }, []);

  useEffect(() => {
    if (roles.length === 0) {
      return;
    }
    setUserFormData(prev => {
      if (prev.role && roles.some(role => role.name === prev.role)) {
        return prev;
      }
      return {
        ...prev,
        role: roles[0].name,
      };
    });
  }, [roles]);

  useEffect(() => {
    if (roles.length && !roles.some(r => r.name === userFormData.role)) {
      setUserFormData(prev => ({ ...prev, role: roles[0].name }));
    }
  }, [roles]);

  const regions = [
    'Addis Ababa',
    'Oromia',
    'Amhara',
    'SNNPR',
    'Tigray',
    'Afar',
    'Somali',
    'Gambela',
    'Harari',
    'Dire Dawa'
  ];

  const formatPermissionLabel = (name, displayName) =>
    displayName || name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const openCreateRoleModal = () => {
    setEditingRole(null);
    setFormData({ name: '', displayName: '', description: '', permissions: [] });
    setNameManuallyEdited(false);
    setShowRoleModal(true);
  };

  const handleRoleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      displayName: role.displayName,
      description: role.description,
      permissions: Array.isArray(role.permissions) ? [...role.permissions] : [],
    });
    setNameManuallyEdited(true);
    setShowRoleModal(true);
  };

  const handleRoleDelete = async (role) => {
    if (!window.confirm(`Delete the role "${role.displayName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await rolesAPI.delete(role.id);
      toast.success('Role deleted successfully');
      const updatedRoles = await fetchRoles();
      await fetchUsers(updatedRoles);
    } catch (error) {
      console.error('Failed to delete role', error);
      toast.error(error.response?.data?.message || 'Failed to delete role');
    }
  };

  const handleUserEdit = (user) => {
    setEditingUser({
      ...user,
      roleDisplayName: getRoleDisplayName(user.role),
    });
    setShowUserModal(true);
  };

  const handlePermissionChange = (permission, checked) => {
    const normalized = permission;
    setFormData(prev => {
      const next = new Set(prev.permissions);
    if (checked) {
        next.add(normalized);
    } else {
        next.delete(normalized);
      }
      return {
        ...prev,
        permissions: Array.from(next),
      };
    });
  };

  const handleRoleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    if (!formData.displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      display_name: formData.displayName.trim(),
      description: formData.description?.trim() || null,
      permissions: Array.from(new Set(formData.permissions)),
      is_active: true,
    };

    setSavingRole(true);
    try {
    if (editingRole) {
        await rolesAPI.update(editingRole.id, payload);
        toast.success('Role updated successfully');
    } else {
        await rolesAPI.create(payload);
        toast.success('Role created successfully');
      }
      const updatedRoles = await fetchRoles();
      await fetchUsers(updatedRoles);
      await refreshUser();
    setShowRoleModal(false);
    setEditingRole(null);
    setFormData({ name: '', displayName: '', description: '', permissions: [] });
      setNameManuallyEdited(false);
    } catch (error) {
      console.error('Failed to save role', error);
      toast.error(error.response?.data?.message || 'Failed to save role');
    } finally {
      setSavingRole(false);
    }
  };

  const handleUserRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.update(userId, { role: newRole });
      toast.success('User role updated');
      await fetchUsers();
      if (user?.id === userId) {
        await refreshUser();
      }
      setEditingUser(prev => prev && prev.id === userId
        ? { ...prev, role: newRole, roleDisplayName: getRoleDisplayName(newRole) }
        : prev);
    } catch (error) {
      console.error('Failed to update user role', error);
      toast.error(error.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleToggleUserStatus = async (userRecord) => {
    try {
      await usersAPI.update(userRecord.id, { is_active: !userRecord.isActive });
      toast.success(!userRecord.isActive ? 'User activated' : 'User deactivated');
      await fetchUsers();
      if (user?.id === userRecord.id) {
        await refreshUser();
      }
      setEditingUser(prev => prev && prev.id === userRecord.id
        ? { ...prev, isActive: !userRecord.isActive }
        : prev);
    } catch (error) {
      console.error('Failed to update user status', error);
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleUserSave = async () => {
    if (editingUser) {
      setShowUserModal(false);
      setEditingUser(null);
      return;
    }

    if (!userFormData.firstName.trim() || !userFormData.lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }
    if (!userFormData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!userFormData.username.trim()) {
      toast.error('Username is required');
      return;
    }

    const password = generatePassword();

    setUserSaving(true);
    try {
      const response = await usersAPI.create({
        username: userFormData.username.trim(),
        email: userFormData.email.trim(),
        password,
        first_name: userFormData.firstName.trim(),
        last_name: userFormData.lastName.trim(),
        role: userFormData.role,
        region: userFormData.region,
        is_active: userFormData.isActive,
      });

      const created = response.data?.user ?? response.data;
      const tempPassword = response.data?.temporary_password ?? password;

      toast.success('User created successfully');
      await fetchUsers();
    setShowUserModal(false);
    setUserFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
        role: roles[0]?.name || 'member',
      region: 'Addis Ababa',
      isActive: true
    });
      setCreatedCredentials({
        name: created?.full_name || `${created?.first_name ?? ''} ${created?.last_name ?? ''}`.trim() || created?.username,
        email: created?.email,
        username: created?.username,
        password: tempPassword,
        roleDisplayName: created?.role_display_name || getRoleDisplayName(created?.role),
      });
    } catch (error) {
      console.error('Failed to create user', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setUserSaving(false);
    }
  };

  const filteredRoles = roles.filter(role => 
    role.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUserInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <RolePermissionContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>Role & Permission Setup</Title>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {activeTab === 'roles' && hasPermission('add_users') && (
            <Button 
              variant="primary" 
              leftIcon={<Plus size={16} />}
              onClick={openCreateRoleModal}
              disabled={loading}
            >
              Add Role
            </Button>
          )}
          {activeTab === 'users' && hasPermission('add_users') && (
            <Button 
              variant="primary" 
              leftIcon={<UserPlus size={16} />}
              onClick={() => {
                setEditingUser(null);
                setUserFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  username: '',
                  role: roles[0]?.name || 'member',
                  region: 'Addis Ababa',
                  isActive: true
                });
                setShowUserModal(true);
              }}
              disabled={usersLoading || roles.length === 0}
            >
              Add User
            </Button>
          )}
        </div>
      </Header>

      <TabsContainer theme={theme}>
        <Tab 
          theme={theme} 
          active={activeTab === 'roles'} 
          onClick={() => setActiveTab('roles')}
        >
          <Shield size={16} style={{ marginRight: '0.5rem' }} />
          Roles & Permissions
        </Tab>
        <Tab 
          theme={theme} 
          active={activeTab === 'users'} 
          onClick={() => setActiveTab('users')}
        >
          <Users size={16} style={{ marginRight: '0.5rem' }} />
          User Management
        </Tab>
      </TabsContainer>

      <ContentArea theme={theme}>
        {loading && (
          <div style={{ color: theme.colors.text.secondary }}>
            Loading role information…
          </div>
        )}
        <SearchContainer theme={theme}>
          <Input
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search size={20} />}
            style={{ minWidth: '300px' }}
          />
        </SearchContainer>

        {activeTab === 'roles' && (
          <div>
            {filteredRoles.map(role => (
              <RoleCard key={role.id} theme={theme}>
                <RoleHeader theme={theme}>
                  <div>
                    <RoleTitle theme={theme}>{role.displayName}</RoleTitle>
                    <RoleDescription theme={theme}>{role.description}</RoleDescription>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                        {role.userCount} users • {role.permissions.length} permissions
                      </span>
                      <RoleBadge theme={theme}>{role.isActive ? 'Active' : 'Inactive'}</RoleBadge>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      variant="outline"
                      size="small"
                      leftIcon={<Eye size={14} />}
                    >
                      View
                    </Button>
                    {hasPermission('edit_users') && (
                      <Button
                        variant="outline"
                        size="small"
                        leftIcon={<Edit size={14} />}
                        onClick={() => handleRoleEdit(role)}
                      >
                        Edit
                      </Button>
                    )}
                    {hasPermission('delete_users') && (
                      <Button
                        variant="outline"
                        size="small"
                        leftIcon={<Trash2 size={14} />}
                        style={{ color: theme.colors.error }}
                        onClick={() => handleRoleDelete(role)}
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </RoleHeader>

                <PermissionGrid theme={theme}>
                  {permissionGroups.map(category => {
                    const entries = category.items && category.items.length
                      ? category.items
                      : (category.permissions || []).map(name => ({ name }));
                    return (
                    <div key={category.category}>
                      <h4 style={{ 
                        fontSize: theme.typography.fontSize.sm, 
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.text.primary,
                        margin: '0 0 0.5rem 0'
                      }}>
                        {category.category}
                      </h4>
                        {entries.map(item => {
                          const permissionName = item.name;
                          const checked = role.permissions.includes(permissionName);
                          return (
                        <PermissionItem 
                              key={permissionName}
                          theme={theme}
                              checked={checked}
                        >
                          <PermissionCheckbox
                            type="checkbox"
                                checked={checked}
                                onChange={() => {}}
                            disabled
                          />
                          <PermissionLabel theme={theme}>
                                {formatPermissionLabel(permissionName, item.display_name)}
                          </PermissionLabel>
                        </PermissionItem>
                          );
                        })}
                    </div>
                    );
                  })}
                </PermissionGrid>
              </RoleCard>
            ))}
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            {usersLoading && (
              <div style={{ color: theme.colors.text.secondary, marginBottom: '1rem' }}>
                Loading users…
              </div>
            )}
            {!usersLoading && filteredUsers.length === 0 && (
              <div style={{ color: theme.colors.text.secondary, marginBottom: '1rem' }}>
                No users found.
              </div>
            )}
            {!usersLoading && filteredUsers.map(user => (
              <UserRoleCard key={user.id} theme={theme}>
                <UserInfo theme={theme}>
                  <UserAvatar theme={theme}>
                    {getUserInitials(user.name)}
                  </UserAvatar>
                  <UserDetails theme={theme}>
                    <UserName theme={theme}>{user.name}</UserName>
                    <UserEmail theme={theme}>{user.email}</UserEmail>
                  </UserDetails>
                  <RoleBadge theme={theme}>{user.roleDisplayName}</RoleBadge>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {hasPermission('edit_users') && (
                      <Button
                        variant="outline"
                        size="small"
                        leftIcon={<Edit size={14} />}
                        onClick={() => handleUserEdit(user)}
                      >
                        Edit Role
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="small"
                      leftIcon={user.isActive ? <Lock size={14} /> : <Unlock size={14} />}
                      onClick={() => handleToggleUserStatus(user)}
                      disabled={usersLoading}
                    >
                      {user.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </UserInfo>
              </UserRoleCard>
            ))}
          </div>
        )}
      </ContentArea>

      {/* Role Edit Modal */}
      <Modal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setEditingRole(null);
          setFormData({ name: '', displayName: '', description: '', permissions: [] });
          setNameManuallyEdited(false);
        }}
        title={editingRole ? 'Edit Role' : 'Add New Role'}
        size="large"
      >
        <ModalContent theme={theme}>
          <FormGroup theme={theme}>
            <Label theme={theme}>Role Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => {
                const value = e.target.value;
                setNameManuallyEdited(true);
                setFormData(prev => ({ ...prev, name: value }));
              }}
              placeholder="e.g., system_admin"
              disabled={!!editingRole}
            />
            <small style={{ color: theme.colors.text.secondary, fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
              Use lowercase letters, numbers, and underscores (e.g., program_officer)
            </small>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Display Name</Label>
            <Input
              value={formData.displayName}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => {
                  const next = { ...prev, displayName: value };
                  if (!editingRole && !nameManuallyEdited) {
                    next.name = slugify(value);
                  }
                  return next;
                });
              }}
              placeholder="e.g., System Administrator"
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Description</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the role's responsibilities..."
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Permissions</Label>
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: `1px solid ${theme.colors.border.light}`, borderRadius: theme.borderRadius.md, padding: '1rem' }}>
              {permissionGroups.length === 0 && (
                <p style={{ color: theme.colors.text.secondary, fontSize: '0.9rem' }}>
                  {loading ? 'Loading permissions…' : 'No permissions available.'}
                </p>
              )}
              {permissionGroups.map(category => {
                const entries = category.items && category.items.length
                  ? category.items
                  : (category.permissions || []).map(name => ({ name }));
                return (
                <div key={category.category} style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ 
                    fontSize: theme.typography.fontSize.base, 
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.text.primary,
                    margin: '0 0 0.5rem 0'
                  }}>
                    {category.category}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                      {entries.map(item => {
                        const permissionName = item.name;
                        const checked = formData.permissions.includes(permissionName);
                        return (
                      <PermissionItem 
                            key={permissionName}
                        theme={theme}
                            checked={checked}
                      >
                        <PermissionCheckbox
                          type="checkbox"
                              checked={checked}
                              onChange={(e) => handlePermissionChange(permissionName, e.target.checked)}
                        />
                        <PermissionLabel theme={theme}>
                              {formatPermissionLabel(permissionName, item.display_name)}
                        </PermissionLabel>
                      </PermissionItem>
                        );
                      })}
                  </div>
                </div>
                );
              })}
            </div>
          </FormGroup>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button 
              variant="outline"
              onClick={() => {
                setShowRoleModal(false);
                setEditingRole(null);
                setFormData({ name: '', displayName: '', description: '', permissions: [] });
                setNameManuallyEdited(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary"
              onClick={handleRoleSave}
              disabled={savingRole}
            >
              {savingRole ? 'Saving...' : (editingRole ? 'Update Role' : 'Create Role')}
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {/* User Role Edit Modal */}
      <Modal
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setEditingUser(null);
          setUserFormData({
            firstName: '',
            lastName: '',
            email: '',
            username: '',
            role: 'member',
            region: 'Addis Ababa',
            isActive: true
          });
        }}
        title={editingUser ? 'Edit User Role' : 'Add New User'}
        size="large"
      >
        <ModalContent theme={theme}>
          {editingUser ? (
            <>
              <FormGroup theme={theme}>
                <Label theme={theme}>User</Label>
                <div style={{ padding: '1rem', backgroundColor: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                  <div style={{ fontWeight: theme.typography.fontWeight.medium }}>{editingUser.name}</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>{editingUser.email}</div>
                </div>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Current Role</Label>
                <RoleBadge theme={theme}>{editingUser.roleDisplayName}</RoleBadge>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Assign New Role</Label>
                <Select
                  theme={theme}
                  value={editingUser.role}
                  onChange={(e) => handleUserRoleChange(editingUser.id, e.target.value)}
                >
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>
                      {role.displayName}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>First Name</Label>
                  <Input
                    value={userFormData.firstName}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Enter first name"
                    required
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Last Name</Label>
                  <Input
                    value={userFormData.lastName}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Enter last name"
                    required
                  />
                </FormGroup>
              </div>

              <FormGroup theme={theme}>
                <Label theme={theme}>Email Address</Label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter email address"
                  required
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Username</Label>
                <Input
                  value={userFormData.username}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Enter username"
                  required
                />
              </FormGroup>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Role</Label>
                  <Select
                    theme={theme}
                    value={userFormData.role}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, role: e.target.value }))}
                  >
                    {roles.map(role => (
                      <option key={role.id} value={role.name}>
                        {role.displayName}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Region</Label>
                  <Select
                    theme={theme}
                    value={userFormData.region}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, region: e.target.value }))}
                  >
                    {regions.map(region => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </div>

              <FormGroup theme={theme}>
                <Label theme={theme}>Account Status</Label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="status"
                      checked={userFormData.isActive}
                      onChange={() => setUserFormData(prev => ({ ...prev, isActive: true }))}
                    />
                    Active
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="status"
                      checked={!userFormData.isActive}
                      onChange={() => setUserFormData(prev => ({ ...prev, isActive: false }))}
                    />
                    Inactive
                  </label>
                </div>
              </FormGroup>
            </>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button 
              variant="outline"
              onClick={() => {
                setShowUserModal(false);
                setEditingUser(null);
                setUserFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  username: '',
                  role: 'member',
                  region: 'Addis Ababa',
                  isActive: true
                });
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary"
              leftIcon={<Save size={16} />}
              onClick={editingUser ? () => {
                setShowUserModal(false);
                setEditingUser(null);
              } : handleUserSave}
              disabled={!editingUser && userSaving}
            >
              {editingUser ? 'Done' : userSaving ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={!!createdCredentials}
        onClose={() => setCreatedCredentials(null)}
        title="New User Credentials"
      >
        <ModalContent theme={theme}>
          {createdCredentials && (
            <>
              <p style={{ color: theme.colors.text.secondary }}>
                Share the credentials below with the user. A copy has also been emailed to them if email delivery is configured.
              </p>
              <div style={{ marginTop: '1rem', display: 'grid', gap: '0.75rem' }}>
                <div>
                  <strong>Name:</strong> {createdCredentials.name}
                </div>
                <div>
                  <strong>Email:</strong> {createdCredentials.email}
                </div>
                <div>
                  <strong>Username:</strong> {createdCredentials.username}
                </div>
                <div>
                  <strong>Temporary Password:</strong> {createdCredentials.password}
                </div>
                <div>
                  <strong>Role:</strong> {createdCredentials.roleDisplayName}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        `Login: ${createdCredentials.username}\nPassword: ${createdCredentials.password}`
                      );
                      toast.success('Credentials copied to clipboard');
                    } catch (error) {
                      toast.error('Unable to copy credentials');
                    }
                  }}
                >
                  Copy Credentials
                </Button>
                <Button variant="primary" onClick={() => setCreatedCredentials(null)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </ModalContent>
      </Modal>
    </RolePermissionContainer>
  );
};

export default RolePermissionSetup;
